/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { CheckCircle2, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from '@/lib/api'
import { cn } from '@/lib/utils'

const STORAGE_KEY = 'trial_feedback_given'

const RATINGS = [
  {
    value: 'beyond_expectations',
    label: 'Beyond expectations',
    emoji: '🤩',
    ring: 'hover:ring-violet-400/50 hover:border-violet-400/60',
    glow: 'hover:shadow-violet-500/20',
  },
  {
    value: 'excellent',
    label: 'Excellent',
    emoji: '😍',
    ring: 'hover:ring-emerald-400/50 hover:border-emerald-400/60',
    glow: 'hover:shadow-emerald-500/20',
  },
  {
    value: 'good',
    label: 'Good',
    emoji: '🙂',
    ring: 'hover:ring-blue-400/50 hover:border-blue-400/60',
    glow: 'hover:shadow-blue-500/20',
  },
  {
    value: 'average',
    label: 'Average',
    emoji: '😐',
    ring: 'hover:ring-amber-400/50 hover:border-amber-400/60',
    glow: 'hover:shadow-amber-500/20',
  },
  {
    value: 'poor',
    label: 'Poor',
    emoji: '😕',
    ring: 'hover:ring-rose-400/50 hover:border-rose-400/60',
    glow: 'hover:shadow-rose-500/20',
  },
] as const

type TrialFeedbackProps = {
  className?: string
}

export function TrialFeedback({ className }: TrialFeedbackProps) {
  const { t } = useTranslation()
  const [submitted, setSubmitted] = useState(
    () => typeof window !== 'undefined' && !!localStorage.getItem(STORAGE_KEY)
  )
  const [submitting, setSubmitting] = useState<string | null>(null)

  const handleRate = async (rating: string) => {
    if (submitting || submitted) return
    setSubmitting(rating)
    try {
      await api.post('/api/trial-feedback', { rating })
      localStorage.setItem(STORAGE_KEY, rating)
      setSubmitted(true)
    } catch {
      // Best-effort; a failed feedback post should never block the user.
    } finally {
      setSubmitting(null)
    }
  }

  if (submitted) {
    return (
      <div
        className={cn(
          'flex items-center justify-center gap-2 rounded-lg bg-emerald-500/10 px-4 py-2.5 text-emerald-600 dark:text-emerald-400',
          className
        )}
      >
        <CheckCircle2 className='size-4 shrink-0' />
        <span className='text-xs font-medium'>
          {t('Thanks for your feedback!')}
        </span>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col items-center gap-2.5', className)}>
      <p className='text-muted-foreground flex items-center gap-1.5 text-xs font-medium'>
        <Sparkles className='size-3.5 text-amber-400' />
        {t('How was your experience?')}
      </p>
      <div className='flex flex-wrap items-center justify-center gap-2'>
        {RATINGS.map((r) => (
          <button
            key={r.value}
            type='button'
            disabled={!!submitting}
            onClick={() => handleRate(r.value)}
            className={cn(
              'group border-border/50 bg-background/80 flex min-w-[4.5rem] flex-col items-center gap-1 rounded-xl border px-3 py-2 shadow-sm backdrop-blur-sm transition-all duration-200 ease-out',
              'hover:-translate-y-0.5 hover:shadow-md hover:ring-2',
              'disabled:pointer-events-none disabled:opacity-40',
              r.ring,
              r.glow
            )}
          >
            <span
              className={cn(
                'text-lg leading-none transition-transform duration-200 group-hover:scale-110',
                submitting === r.value && 'animate-pulse'
              )}
            >
              {r.emoji}
            </span>
            <span className='text-foreground/70 group-hover:text-foreground text-[10px] leading-tight font-medium'>
              {t(r.label)}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}
